// Per-dialect synthesizer for FOREIGN KEY add/drop ops (v2 Slice B2).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice B2.
//
// Each engine emits the dialect-correct ADD/DROP CONSTRAINT clause:
//   - mssql:  `ADD [CONSTRAINT name] FOREIGN KEY (cols) REFERENCES <ref> (refCols)
//             [ON DELETE …] [ON UPDATE …] [WITH NOCHECK]`
//             / `DROP CONSTRAINT name`. RESTRICT, MATCH, DEFERRABLE rejected.
//   - pgsql:  full set including `MATCH {FULL|PARTIAL|SIMPLE}` and
//             `DEFERRABLE INITIALLY {DEFERRED|IMMEDIATE}` / `DROP CONSTRAINT
//             name [CASCADE]`.
//   - mysql:  `ADD CONSTRAINT name FOREIGN KEY (cols) REFERENCES <ref> (refCols)
//             [ON DELETE …] [ON UPDATE …]` / `DROP FOREIGN KEY name`. SET
//             DEFAULT, MATCH, DEFERRABLE rejected.
//   - oracle: `ADD CONSTRAINT name FOREIGN KEY (cols) REFERENCES <ref> (refCols)
//             [ON DELETE {CASCADE|SET NULL}] [DEFERRABLE INITIALLY …]`
//             / `DROP CONSTRAINT name [CASCADE]`. ON UPDATE, RESTRICT, SET
//             DEFAULT, MATCH rejected.
//   - sqlite: not supported on ALTER → throws (DSE2-6). FK in CREATE TABLE
//             only; there is no automated recreate helper for this op (the
//             opt-in `sqliteRecreate` primitive only rewrites columns) — the
//             table must be rebuilt by hand.

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type { ForeignKeyDef, ReferentialAction } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';
import { type ExistingNameOrigin, nameExistingColumn } from './dialect.quoting';
import type { ColumnListOrigins } from './existingNameEvidence';

export interface ForeignKeySynthOpts {
  /** The DSE6 STYLE flag — the host relation's own quoting, mirrored onto the
   *  column tokens. A style may ADD delimiters; it may never decide WHICH
   *  columns are named. See `columnNameOrigin`. */
  readonly quoteName: boolean;
  /** WHERE the spelling at each POSITION of `fk.columns` came from. A FOREIGN
   *  KEY names columns the catalog ALREADY HOLDS, so every element sits on the
   *  same three-way contract (`dialect.quoting.ts`'s
   *  {@link ExistingNameOrigin}) as a rename's old name and a drop's target,
   *  and `quoteName` alone cannot answer it. Asked PER ELEMENT because one
   *  list may legitimately hold a source-delimited `"Id"` next to a bare
   *  `target_col`.
   *
   *  It is deliberately NOT applied to `fk.refColumns`: those name columns of
   *  `fk.refTable`, a DIFFERENT relation as far as this synthesizer can tell.
   *  A coincidental name match on THIS table's clauses would otherwise declare
   *  the referenced table's spelling. See `refColumnNameOrigin`. */
  readonly columnNameOrigin?: ColumnListOrigins;
  /** WHERE the spelling at each POSITION of `fk.refColumns` came from. Same
   *  three-way contract, asked separately because it is asked ABOUT ANOTHER
   *  RELATION: `fk.refTable` is verbatim caller text and this synthesizer
   *  never reads it, so it cannot tell whose columns these are. It defaults to
   *  `'bare-reference'` and only a caller that has ESTABLISHED the referenced
   *  relation may pass anything else.
   *
   *  The case that makes the separation earn its keep is the SELF-REFERENCING
   *  key — a tree table's `parent_id → id`, the single commonest FK shape
   *  there is. There the referenced relation IS the source's own, so the
   *  source statement speaks for those columns exactly as it does for the
   *  owner-side list: after `ALTER TABLE "t" ADD COLUMN "Id" integer PRIMARY
   *  KEY`, a `refColumns: ['Id']` folded to `REFERENCES t ("id")` names a
   *  column the quoted ADD never created. `foreignKeyEdit.ts` owns that proof
   *  (`namesSameRelation` over the parsed `refTable`); everything it cannot
   *  prove keeps the default. */
  readonly refColumnNameOrigin?: ColumnListOrigins;
}

/** The default origin resolver: every element is a reference the caller wrote
 *  bare, which is what a free-text column list is until the source statement
 *  says otherwise. */
const BARE_REFERENCE = (): ExistingNameOrigin => 'bare-reference';

function tableText(engine: SqlEngine, table: QualifiedName): string {
  if (table.raw && table.raw.length > 0) return table.raw;
  const name = maybeQuoteIdent(engine, table.name, table.quoted);
  if (table.schema) {
    const schema = maybeQuoteIdent(engine, table.schema, table.quoted);
    return `${schema}.${name}`;
  }
  return name;
}

/** Name each column the constraint lists. `styleDelimits` may only ADD
 *  delimiters; `nameExistingColumn` picks the TEXT that goes inside them per
 *  element, because adding delimiters to a bare reference suppresses the very
 *  fold that was resolving it, and folding a spelling the server already
 *  stores names a different column.
 *
 *  The origin is asked for by POSITION: two elements of one list may carry the
 *  same bytes (`Id` and `"Id"` decode alike) and only their positions tell
 *  them apart. */
function quoteCols(
  engine: SqlEngine,
  cols: readonly string[],
  styleDelimits: boolean,
  originAt: ColumnListOrigins,
): string {
  return cols.map((c, i) => {
    const { text, delimit } = nameExistingColumn(c, engine, originAt(i), styleDelimits);
    return maybeQuoteIdent(engine, text, delimit);
  }).join(', ');
}

function rejectField(engine: SqlEngine, field: string): never {
  throw new Error(
    `applyDdlEdit: ${engine} FOREIGN KEY does not accept '${field}'`,
  );
}

function rejectAction(engine: SqlEngine, slot: 'onDelete' | 'onUpdate', action: ReferentialAction): never {
  throw new Error(
    `applyDdlEdit: ${engine} FOREIGN KEY '${slot}' does not accept '${action}'`,
  );
}

function actionKeyword(action: ReferentialAction): string {
  switch (action) {
    case 'no-action':   return 'NO ACTION';
    case 'restrict':    return 'RESTRICT';
    case 'cascade':     return 'CASCADE';
    case 'set-null':    return 'SET NULL';
    case 'set-default': return 'SET DEFAULT';
  }
}

function validateAction(
  engine: SqlEngine,
  slot: 'onDelete' | 'onUpdate',
  action: ReferentialAction,
): void {
  if (engine === 'mssql') {
    if (action === 'restrict') rejectAction(engine, slot, action);
    return;
  }
  if (engine === 'mysql') {
    if (action === 'set-default') rejectAction(engine, slot, action);
    return;
  }
  if (engine === 'oracle') {
    if (action === 'restrict' || action === 'set-default' || action === 'no-action') {
      rejectAction(engine, slot, action);
    }
    return;
  }
  // postgres accepts all 5.
}

function validateFk(engine: SqlEngine, fk: ForeignKeyDef): void {
  if (fk.columns.length === 0) {
    throw new Error(`applyDdlEdit: FOREIGN KEY requires at least one column`);
  }
  if (fk.refColumns.length === 0) {
    throw new Error(`applyDdlEdit: FOREIGN KEY requires at least one refColumn`);
  }
  if (fk.columns.length !== fk.refColumns.length) {
    throw new Error(
      `applyDdlEdit: FOREIGN KEY columns (${fk.columns.length}) and refColumns `
        + `(${fk.refColumns.length}) must have the same length`,
    );
  }
  if (!fk.refTable || fk.refTable.length === 0) {
    throw new Error(`applyDdlEdit: FOREIGN KEY requires 'refTable'`);
  }
  if (engine !== 'postgres' && fk.match !== undefined) rejectField(engine, 'match');
  if (engine !== 'postgres' && engine !== 'oracle' && fk.deferrable !== undefined) {
    rejectField(engine, 'deferrable');
  }
  if (engine !== 'mssql' && fk.mssqlWithNoCheck !== undefined) {
    rejectField(engine, 'mssqlWithNoCheck');
  }
  if (engine === 'oracle' && fk.onUpdate !== undefined) {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: oracle FOREIGN KEY does not support ON UPDATE — Oracle `
        + `has no ON UPDATE referential action.`,
    );
  }
  if (fk.onDelete !== undefined) validateAction(engine, 'onDelete', fk.onDelete);
  if (fk.onUpdate !== undefined) validateAction(engine, 'onUpdate', fk.onUpdate);
}

function matchKeyword(match: 'full' | 'partial' | 'simple'): string {
  switch (match) {
    case 'full':    return 'FULL';
    case 'partial': return 'PARTIAL';
    case 'simple':  return 'SIMPLE';
  }
}

function deferrableClause(deferrable: 'deferred' | 'immediate'): string {
  return ` DEFERRABLE INITIALLY ${deferrable === 'deferred' ? 'DEFERRED' : 'IMMEDIATE'}`;
}

export function synthesizeAddForeignKey(
  engine: SqlEngine,
  table: QualifiedName,
  fk: ForeignKeyDef,
  opts: ForeignKeySynthOpts,
): string {
  if (engine === 'sqlite') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: SQLite does not support ALTER TABLE … ADD CONSTRAINT `
        + `FOREIGN KEY. Define the foreign key inline at CREATE TABLE time, or `
        + `rebuild the table manually (CREATE TABLE with the constraint, copy `
        + `the data across, DROP the old table, RENAME the new one) — the `
        + `opt-in 'sqliteRecreate' path only rewrites columns `
        + `(add/drop/rename/alter), not table-level constraints.`,
    );
  }
  validateFk(engine, fk);

  const tbl = tableText(engine, table);
  const cols = quoteCols(
    engine, fk.columns, opts.quoteName, opts.columnNameOrigin ?? BARE_REFERENCE,
  );
  // The ref side belongs to `fk.refTable`, so it answers on its OWN resolver —
  // see `refColumnNameOrigin` for why that is a separate question and when a
  // caller may answer it with anything but the bare-reference default.
  const refCols = quoteCols(
    engine, fk.refColumns, opts.quoteName, opts.refColumnNameOrigin ?? BARE_REFERENCE,
  );
  const constraintPrefix = fk.name ? `CONSTRAINT ${fk.name} ` : '';

  let s = `ALTER TABLE ${tbl} ADD ${constraintPrefix}FOREIGN KEY (${cols}) REFERENCES ${fk.refTable} (${refCols})`;

  if (engine === 'postgres' && fk.match !== undefined) {
    s += ` MATCH ${matchKeyword(fk.match)}`;
  }
  if (fk.onDelete !== undefined) s += ` ON DELETE ${actionKeyword(fk.onDelete)}`;
  if (fk.onUpdate !== undefined) s += ` ON UPDATE ${actionKeyword(fk.onUpdate)}`;
  if ((engine === 'postgres' || engine === 'oracle') && fk.deferrable !== undefined) {
    s += deferrableClause(fk.deferrable);
  }
  if (engine === 'mssql' && fk.mssqlWithNoCheck) s += ` WITH NOCHECK`;
  return s;
}

export function synthesizeDropForeignKey(
  engine: SqlEngine,
  table: QualifiedName,
  constraintName: string,
  cascade: boolean | undefined,
): string {
  if (engine === 'sqlite') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: SQLite does not support ALTER TABLE … DROP FOREIGN KEY. `
        + `Rebuild the table manually (CREATE TABLE without the constraint, `
        + `copy the data across, DROP the old table, RENAME the new one) — `
        + `the opt-in 'sqliteRecreate' path only rewrites columns `
        + `(add/drop/rename/alter), not table-level constraints.`,
    );
  }
  if (!constraintName || constraintName.length === 0) {
    throw new Error(
      `applyDdlEdit: ${engine} DROP FOREIGN KEY requires 'constraintName'`,
    );
  }
  const tbl = tableText(engine, table);

  if (engine === 'mysql') {
    // mysql uses `DROP FOREIGN KEY <name>` (not DROP CONSTRAINT). CASCADE is
    // not accepted in this position on mysql — silently ignored for API parity.
    return `ALTER TABLE ${tbl} DROP FOREIGN KEY ${constraintName}`;
  }

  const cas = (engine === 'postgres' || engine === 'oracle') && cascade ? ' CASCADE' : '';
  return `ALTER TABLE ${tbl} DROP CONSTRAINT ${constraintName}${cas}`;
}
